import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { leaderboardAPI } from '../lib/api';
import api from '../lib/api';
import { motion } from 'framer-motion';
import { Skeleton } from '../components/ui/skeleton';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Trophy, TrendingUp, Minus, ChevronDown, Pin, Flag } from 'lucide-react';
import { toast } from 'sonner';
import ShareButton from '../components/ShareButton';
import { AnimatedNumber } from '../components/AnimatedNumber';
import { RankDeltaBadge } from '../components/RankDeltaBadge';
import { computeLeaderboardDeltas } from '../lib/rankAnim';
import { AffiliationDisclaimer } from '../components/LegalDisclaimer';
import { formatFranc, formatFrancAdaptive } from '../lib/utils';
import { QuarterRecapCard } from '../components/QuarterRecapCard';
import { lastDayLabel } from '../components/LeagueClosedNote';

const DICEBEAR_BASE = 'https://api.dicebear.com/7.x';

// Build a row's avatar from server data. For the current user we prefer the
// freshest AuthContext copy (`me`) so a just-changed avatar shows immediately.
function buildRankAvatar(team, me) {
  const src = (me && team.user_id === me.id) ? me : team;
  if (src?.avatar_type === 'photo' && src?.avatar_url) {
    return src.avatar_url.startsWith('http')
      ? src.avatar_url
      : `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}${src.avatar_url}`;
  }
  if (src?.avatar_type === 'dicebear' && src?.avatar_style && src?.avatar_seed) {
    return `${DICEBEAR_BASE}/${src.avatar_style}/svg?seed=${encodeURIComponent(src.avatar_seed)}&size=64&backgroundColor=transparent`;
  }
  return `${DICEBEAR_BASE}/initials/svg?seed=${encodeURIComponent(team.username || team.name || '?')}&size=64&backgroundColor=transparent`;
}
const PAGE_SIZE = 20;

// PERF (2026-07 UX audit P1) — module-scoped stale-while-revalidate cache
// (same pattern as NewsTab's _newsCache): every Rankings visit used to be a
// full cold load with a skeleton flash. Revisits render instantly and
// refresh silently in the background.
const _rankCache = { key: null, leaderboard: null, myRankInfo: null, gw: null };

function PodiumCard({ team, rank, onClick, prevPoints, me, final = false }) {
  // 2026-07-20 perf audit — avatar URL string built once per team identity,
  // not on every AnimatedNumber tick re-render.
  const avatarUrl = useMemo(() => (team ? buildRankAvatar(team, me) : null), [team, me]);
  const heights = { 1: 'h-28', 2: 'h-24', 3: 'h-20' };
  const medals = { 1: 'bg-yellow-400', 2: 'bg-gray-300', 3: 'bg-amber-600' };
  const order = { 1: 'order-2', 2: 'order-1', 3: 'order-3' };
  if (!team) {
    // 2026-07-23 (owner request) — empty pedestal keeps the podium triangle
    // when only 1-2 teams are finalized, inviting players to claim the spot.
    return (
      <div
        className={`flex flex-col items-center ${order[rank]} opacity-70`}
        data-testid={`podium-empty-${rank}`}
      >
        <div className="relative mb-2">
          <div className="h-12 w-12 rounded-full bg-card border-2 border-dashed border-border/70" />
          <div className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-muted flex items-center justify-center text-[10px] font-bold text-muted-foreground shadow-sm">
            {rank}
          </div>
        </div>
        <p className="text-xs font-semibold text-center text-muted-foreground">Open spot</p>
        <p className="text-[10px] text-muted-foreground/70">Finalize to claim</p>
        <div className={`mt-2 w-20 ${heights[rank]} rounded-t-xl bg-card/50 border border-dashed border-border/50 flex items-center justify-center px-1`}>
          <p className="text-sm font-bold text-muted-foreground/40">—</p>
        </div>
      </div>
    );
  }
  return (
    <div
      className={`flex flex-col items-center ${order[rank]} cursor-pointer rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring`}
      role="button"
      tabIndex={0}
      aria-label={`View ${team?.name || 'team'}, rank ${rank}`}
      onClick={() => onClick?.(team?.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(team?.id); } }}
      data-testid={`podium-card-${rank}`}
    >
      <div className="relative mb-2">
        <img
          src={avatarUrl}
          alt=""
          className="h-12 w-12 rounded-full bg-card border-2 border-border object-cover"
          data-testid={`podium-avatar-${rank}`}
        />
        <div className={`absolute -bottom-1 -right-1 h-5 w-5 rounded-full ${medals[rank]} flex items-center justify-center text-[10px] font-bold text-white shadow-sm`}>
          {rank}
        </div>
      </div>
      <p className="text-xs font-semibold text-center truncate max-w-[80px]">{team?.name || 'Unknown'}</p>
      <p className="text-[10px] text-muted-foreground">{team?.username || ''}</p>
      <div className={`mt-2 w-20 ${heights[rank]} rounded-t-xl bg-card border border-border/60 flex flex-col items-center justify-center gap-0.5 px-1`}>
        <p className="text-sm font-bold tabular-nums truncate max-w-[72px] text-center">
          <AnimatedNumber
            value={team?.net_worth || 0}
            from={typeof prevPoints === 'number' ? prevPoints : undefined}
            format={(n) => formatFrancAdaptive(n, { tight: true })}
            durationMs={1000}
            testid={`podium-points-${rank}`}
          />
        </p>
        {/* Build 4 — today's gain on the podium (web/mobile parity); hidden on a frozen board */}
        {final ? null : (team?.daily_points || 0) >= 0.005 ? (
          <span className="badge-positive text-[9px] px-1.5 py-0 rounded-full inline-flex items-center gap-0.5" data-testid={`podium-gain-${rank}`} title="Earned today (since midnight UTC)">
            <TrendingUp className="h-2.5 w-2.5" />{formatFranc(team.daily_points, { sign: true })}
          </span>
        ) : (
          <span className="text-[9px] text-muted-foreground" data-testid={`podium-gain-${rank}`}>0 today</span>
        )}
      </div>
    </div>
  );
}

function RankRow({ team, rank, onClick, currentUserId, me, onReport, pinned = false, delta = 0, prevPoints, final = false }) {
  const isMe = team.user_id === currentUserId;
  const avatarUrl = useMemo(() => buildRankAvatar(team, me), [team, me]);
  const canReport = !isMe && team.avatar_type === 'photo' && team.user_id;

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-xl border bg-card cursor-pointer hover:shadow-sm hover:border-border/80 active:scale-[0.99] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        isMe ? 'border-[hsl(var(--accent)/0.4)] bg-[hsl(var(--accent)/0.06)]' : 'border-border/60'
      } ${isMe && delta ? 'rank-glow' : ''}`}
      role="button"
      tabIndex={0}
      aria-label={`View ${team.name || 'team'}, rank ${rank}`}
      data-testid={pinned ? 'rank-row-pinned-self' : `rank-row-${rank}`}
      onClick={() => onClick?.(team.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(team.id); } }}
    >
      <div className="w-12 shrink-0 text-center flex flex-col items-center">
        <span className={`text-sm font-bold tabular-nums whitespace-nowrap ${isMe ? 'text-foreground' : 'text-muted-foreground'}`}>
          {rank}
        </span>
        <RankDeltaBadge delta={delta} testid={pinned ? 'rank-delta-pinned-self' : `rank-delta-${rank}`} />
        {pinned && <Pin className="h-2.5 w-2.5 text-[hsl(var(--accent))] mt-0.5" fill="currentColor" />}
      </div>
      <img src={avatarUrl} alt="" loading="lazy" className="h-8 w-8 rounded-full bg-secondary" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{team.name || 'Unknown Team'}</p>
        <p className="text-xs text-muted-foreground truncate">{team.username || 'Anonymous'}</p>
      </div>
      {canReport && (
        <button
          onClick={(e) => { e.stopPropagation(); onReport?.(team); }}
          className="shrink-0 p-1.5 text-muted-foreground/50 hover:text-destructive transition-colors"
          title="Report photo"
          aria-label="Report photo"
          data-testid={`rank-report-${team.user_id}`}
        >
          <Flag className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="text-right">
        <p className="text-sm font-bold tabular-nums">
          <AnimatedNumber
            value={team?.net_worth || 0}
            from={typeof prevPoints === 'number' ? prevPoints : undefined}
            format={(n) => formatFrancAdaptive(n, { tight: true })}
            durationMs={900}
            testid={pinned ? 'rank-points-pinned-self' : `rank-points-${rank}`}
          />
        </p>
        {final ? null : (team.daily_points || 0) >= 0.005 ? (
          <span className="badge-positive text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5" data-testid={pinned ? 'rank-gain-pinned-self' : `rank-gain-${rank}`} title="Earned today (since midnight UTC)">
            <TrendingUp className="h-2.5 w-2.5" />{formatFranc(team.daily_points, { sign: true })}
          </span>
        ) : (
          <span className="text-[10px] text-muted-foreground" data-testid={pinned ? 'rank-gain-pinned-self' : `rank-gain-${rank}`}><Minus className="h-2.5 w-2.5 inline" /> 0 today</span>
        )}
      </div>
    </div>
  );
}

export default function RankingsTab({ selectedLeagueId, onTeamClick, onFinishTeam }) {
  const { user } = useAuth();

  // Report another player's custom photo → admin moderation queue.
  const handleReportPhoto = useCallback(async (team) => {
    if (!window.confirm(`Report @${team.username || 'this player'}'s photo as inappropriate?`)) return;
    try {
      await api.post('/profile/report', { target_user_id: team.user_id, reason: 'inappropriate' });
      toast.success('Reported. Thanks — our team will review it.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not report');
    }
  }, []);
  const cacheKey = `${selectedLeagueId || 'global'}:${user?.id || 'anon'}`;
  const hasCache = _rankCache.key === cacheKey && Array.isArray(_rankCache.leaderboard);
  const [leaderboard, setLeaderboard] = useState(hasCache ? _rankCache.leaderboard : []);
  const [loading, setLoading] = useState(!hasCache);
  const [loadingMore, setLoadingMore] = useState(false);
  const [gw, setGw] = useState(hasCache ? _rankCache.gw : null);
  const [myRankInfo, setMyRankInfo] = useState(hasCache ? _rankCache.myRankInfo : null); // {team, rank, total_teams}
  // Build 4 — "X competing" badge (finalized teams on this board).
  const [competingTotal, setCompetingTotal] = useState(hasCache ? _rankCache.competingTotal ?? null : null);
  const [finalInfo, setFinalInfo] = useState(hasCache ? _rankCache.finalInfo ?? null : null);
  const [myRecap, setMyRecap] = useState(null);
  const [reachedEnd, setReachedEnd] = useState(false);
  // v6.x — rank/points delta + "shuffle" animation state.
  const [rankDeltas, setRankDeltas] = useState({});
  const [orderMode, setOrderMode] = useState('current'); // 'prev' | 'current'
  const prevRanksRef = useRef(null);
  const prevPointsRef = useRef(null);
  const shuffleTimerRef = useRef(null);
  // Guard: the delta/snapshot computation has a WRITE side-effect, so it must
  // run exactly once per (league,user). loadInitial re-fires as deps settle.
  const snapshotDoneForRef = useRef(null);
  // A newer loadInitial (league switch / header settling) wins; stale runs drop their results.
  const loadSeqRef = useRef(0);
  useEffect(() => () => { if (shuffleTimerRef.current) clearTimeout(shuffleTimerRef.current); }, []);

  // v5.7.63 — Resolve the actual league_id we're querying. The
  // /leaderboard/global/top endpoint maps to the FIRST league with
  // league_type=global, and the my-rank endpoint needs the same
  // resolved id. Skip my-rank for now if no league context.
  const loadInitial = useCallback(async () => {
    // SWR: only show the skeleton when we have nothing cached for this
    // league+user — otherwise the cached board stays visible while we
    // revalidate in the background.
    const key = `${selectedLeagueId || 'global'}:${user?.id || 'anon'}`;
    const seq = ++loadSeqRef.current;
    const stale = () => seq !== loadSeqRef.current;
    const cached = _rankCache.key === key && Array.isArray(_rankCache.leaderboard);
    if (!cached) setLoading(true);
    setReachedEnd(false);
    try {
      // Top page in parallel with gameweek AND (when the league id is
      // already known) my-rank. 2026-07 audit P1 — my-rank previously
      // ALWAYS waited for the top page; only the global view (which must
      // resolve the league id from the top response) still needs to.
      const [topRes, gwRes, myResEarly] = await Promise.all([
        selectedLeagueId
          ? leaderboardAPI.get(selectedLeagueId, { limit: PAGE_SIZE, offset: 0 })
          : leaderboardAPI.global({ limit: PAGE_SIZE, offset: 0 }),
        api.get('/gameweek/current', { params: selectedLeagueId ? { league_id: selectedLeagueId } : {} }).catch(() => null),
        selectedLeagueId ? leaderboardAPI.myRank(selectedLeagueId).catch(() => null) : Promise.resolve(null),
      ]);
      if (stale()) return;
      const lb = topRes.data.leaderboard || [];
      setLeaderboard(lb);
      if (typeof topRes.data.competing_total === 'number') {
        setCompetingTotal(topRes.data.competing_total);
        // Keep the header's "N competing" pill on the same number as this board.
        window.dispatchEvent(new CustomEvent('fws:competing-total', {
          detail: { leagueId: topRes.data.league_id || selectedLeagueId, total: topRes.data.competing_total },
        }));
      }
      if (lb.length < PAGE_SIZE) setReachedEnd(true);
      if (gwRes) setGw(gwRes.data);
      _rankCache.key = key;
      _rankCache.leaderboard = lb;
      _rankCache.competingTotal = topRes.data.competing_total ?? null;
      // 2026-11-01 — a closed league's board is its Final standings.
      const fi = topRes.data.final ? { endedAt: topRes.data.ended_at } : null;
      setFinalInfo(fi);
      _rankCache.finalInfo = fi;
      if (fi && user?.id && (topRes.data.league_id || selectedLeagueId)) {
        api.get(`/recaps/league/${topRes.data.league_id || selectedLeagueId}`)
          .then((r) => { if (!stale()) setMyRecap(r?.data?.recap || null); })
          .catch(() => { if (!stale()) setMyRecap(null); });
      } else {
        setMyRecap(null);
      }
      if (gwRes) _rankCache.gw = gwRes.data;

      // v6.x — compute rank/points deltas vs the user's LAST visit and, if
      // anything moved, briefly render the previous order then let
      // framer-motion slide the rows into their new spots (leaderboard
      // "shuffle"). Run EXACTLY ONCE per (league,user) per mount:
      // computeLeaderboardDeltas writes the new baseline, so a second call
      // for the same key would read the just-written values, see no change,
      // and wipe the badges + cancel the shuffle.
      const snapKey = `rankFull:${selectedLeagueId || 'global'}:${user?.id || 'anon'}`;
      if (snapshotDoneForRef.current !== snapKey) {
        snapshotDoneForRef.current = snapKey;
        const { deltas, prevRanks, prevPoints, anyMoved } = computeLeaderboardDeltas(lb, snapKey);
        prevRanksRef.current = prevRanks;
        prevPointsRef.current = prevPoints;
        setRankDeltas(deltas);
        if (shuffleTimerRef.current) clearTimeout(shuffleTimerRef.current);
        if (anyMoved) {
          setOrderMode('prev');
          // Hold the previous order briefly so the user registers it, then
          // flip → framer-motion slides every row to its new spot.
          shuffleTimerRef.current = setTimeout(() => setOrderMode('current'), 550);
        } else {
          setOrderMode('current');
        }
      }

      // Resolve the actual league_id (global endpoint returns it in the body)
      const resolvedId = topRes.data.league_id || selectedLeagueId;
      if (myResEarly) {
        setMyRankInfo(myResEarly.data);
        _rankCache.myRankInfo = myResEarly.data;
      } else if (resolvedId) {
        try {
          const myRes = await leaderboardAPI.myRank(resolvedId);
          if (stale()) return;
          setMyRankInfo(myRes.data);
          _rankCache.myRankInfo = myRes.data;
        } catch (e) {
          if (stale()) return;
          // No team / unauthenticated — fall back to client-side scan
          const mineInPage = lb.find(t => t.user_id === user?.id);
          const fallback = mineInPage ? { team: mineInPage, rank: mineInPage.rank, total_teams: lb.length } : null;
          setMyRankInfo(fallback);
          _rankCache.myRankInfo = fallback;
        }
      }
    } catch (e) {
      console.error('Failed to load rankings:', e);
    } finally {
      if (!stale()) setLoading(false);
    }
  }, [selectedLeagueId, user?.id]);

  useEffect(() => { loadInitial(); }, [loadInitial]);

  const loadMore = async () => {
    if (loadingMore || reachedEnd) return;
    setLoadingMore(true);
    try {
      const offset = leaderboard.length;
      const res = selectedLeagueId
        ? await leaderboardAPI.get(selectedLeagueId, { limit: PAGE_SIZE, offset })
        : await leaderboardAPI.global({ limit: PAGE_SIZE, offset });
      const more = res.data.leaderboard || [];
      // Dedupe by id (defensive — same team should never appear twice
      // since both pages use the same sort, but if backend ranks change
      // between requests we don't want duplicate keys in React).
      const seen = new Set(leaderboard.map(t => t.id));
      const fresh = more.filter(t => !seen.has(t.id));
      setLeaderboard(prev => [...prev, ...fresh]);
      if (more.length < PAGE_SIZE) setReachedEnd(true);
    } catch (e) {
      console.error('Load more failed:', e);
    } finally {
      setLoadingMore(false);
    }
  };

  // 2026-07-23 (owner request) — the podium now renders whenever ANY team is
  // finalized, padding missing ranks with "Open spot" pedestals (mobile
  // parity). `rest` stays slice(3) — ranks 1-3 always live on the podium.
  const top3 = leaderboard.slice(0, 3);
  const rest = leaderboard.slice(3);

  // v6.x — during the 'prev' phase, lay rows out in their PREVIOUS order so
  // the flip to 'current' animates the slide. Teams with no prior snapshot
  // keep their current spot (they fade in place rather than fly in).
  const restOrdered = useMemo(() => {
    if (orderMode === 'prev' && prevRanksRef.current) {
      return [...rest].sort((a, b) =>
        (prevRanksRef.current[a.id] ?? a.rank ?? 9e9) - (prevRanksRef.current[b.id] ?? b.rank ?? 9e9)
      );
    }
    return rest;
  }, [rest, orderMode]);

  // v5.7.63 — Only pin the self-row at the bottom if the user actually
  // has a team in this league (myRankInfo.team is non-null). If their
  // rank already appears in the loaded set we still pin a duplicate
  // row — explicit per user requirement ("no harm done can keep
  // redundant, static row at the bottom").
  const pinnedSelf = myRankInfo?.team || null;

  if (loading) {
    return (
      <div className="p-4 space-y-3" data-testid="rankings-tab">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full rounded-2xl" />
        {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-2xl" />)}
      </div>
    );
  }

  return (
    <div className="p-4" data-testid="rankings-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>
          <Trophy className="h-5 w-5 inline mr-2 text-[hsl(43,74%,52%)]" />
          Rankings
          {finalInfo && (
            <span
              className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wider text-[hsl(var(--accent-text))] bg-[hsl(var(--accent)/0.12)] border border-[hsl(var(--accent)/0.35)] px-2 py-0.5 rounded-full"
              data-testid="rankings-final-badge"
            >
              Final standings
            </span>
          )}
          {typeof competingTotal === 'number' && (
            <span
              className="ml-2 align-middle text-[10px] font-semibold text-muted-foreground bg-secondary/60 border border-border/60 px-2 py-0.5 rounded-full"
              title={finalInfo ? 'Everyone who had a team when the league ended' : 'Teams that finalized their roster and appear on this leaderboard'}
              data-testid="rankings-competing-badge"
            >
              {competingTotal} {finalInfo ? 'players' : 'competing'}
            </span>
          )}
        </h2>
        <ShareButton
          path={`/league/${selectedLeagueId || 'global'}`}
          title="Fantasy World Star League"
          text="Check out this league's standings on Fantasy World Star"
          iconOnly
          testid="share-league-btn"
        />
      </div>

      {/* v5.7.48 — Per-league game-week label */}
      {finalInfo ? (
        <p className="text-xs text-muted-foreground mb-3 px-1" data-testid="rankings-final-label">
          This league has ended — everyone who had a team, frozen as it stood
          {lastDayLabel(finalInfo.endedAt) ? <> on <b className="text-foreground">{lastDayLabel(finalInfo.endedAt)}</b></> : ' on its last day'}.
        </p>
      ) : gw?.total_weeks && (
        <p className="text-xs text-muted-foreground mb-3 px-1" data-testid="rankings-gameweek-label">
          {gw.league_status === 'pre_start'
            ? <>League starts <b className="text-foreground">{new Date(gw.scoring_starts_at).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}</b> — no scores yet.</>
            : <>Game Week <b className="text-foreground">{gw.gameweek}</b> of {gw.total_weeks}</>}
        </p>
      )}

      {finalInfo && myRecap && (
        <div className="mb-5">
          <QuarterRecapCard recap={myRecap} testid="rankings-final-recap-card" />
        </div>
      )}

      {/* Podium for top 3 — empty ranks render as claimable pedestals */}
      {top3.length > 0 && (
        <div className="flex items-end justify-center gap-4 mb-6 px-4">
          {[1, 2, 3].map((rank) => (
            <PodiumCard
              key={top3[rank - 1]?.id || `empty-${rank}`}
              team={top3[rank - 1]}
              rank={rank}
              onClick={onTeamClick}
              me={user}
              final={!!finalInfo}
              prevPoints={prevPointsRef.current?.[top3[rank - 1]?.id]}
            />
          ))}
        </div>
      )}

      {/* Rest of leaderboard. The body scrolls naturally; we add extra
          bottom padding so the last rows clear the fixed "Your team" bar
          (which is pinned above the tab bar, like the nav itself). */}
      <div className="space-y-2" data-testid="rankings-table" style={{ paddingBottom: pinnedSelf ? '160px' : '24px' }}>
        {leaderboard.length === 0 && (
          <div className="text-center py-12">
            <Trophy className="h-10 w-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-muted-foreground text-sm">{finalInfo ? 'Nobody had a team in this league.' : 'No rankings yet. Start drafting to compete!'}</p>
          </div>
        )}
        {restOrdered.map((team) => {
          // During the 'prev' hold show the row's PREVIOUS rank (and hide the
          // delta chip) so the board reads as the OLD standings; on flip it
          // slides + the number updates + the ▲/▼ chip appears.
          const pr = prevRanksRef.current?.[team.id];
          const showingPrev = orderMode === 'prev' && pr != null;
          return (
            <motion.div
              key={team.id}
              // 2026-07-20 perf audit — `layout` on EVERY row made framer
              // measure the whole list each render; enable FLIP only for rows
              // whose rank actually changed between snapshots.
              layout={pr != null && pr !== team.rank}
              transition={{ type: 'spring', stiffness: 260, damping: 30 }}
            >
              <RankRow
                team={team}
                rank={showingPrev ? pr : team.rank}
                onClick={onTeamClick}
                currentUserId={user?.id}
                me={user}
                onReport={handleReportPhoto}
                final={!!finalInfo}
                delta={showingPrev ? 0 : rankDeltas[team.id]}
                prevPoints={prevPointsRef.current?.[team.id]}
              />
            </motion.div>
          );
        })}
        {!reachedEnd && leaderboard.length > 0 && (
          <Button
            variant="outline"
            className="w-full"
            onClick={loadMore}
            disabled={loadingMore}
            data-testid="rankings-load-more"
          >
            {loadingMore ? 'Loading…' : (
              <><ChevronDown className="h-4 w-4 mr-1" /> Load more</>
            )}
          </Button>
        )}
        {reachedEnd && leaderboard.length > PAGE_SIZE && (
          <p className="text-center text-[10px] text-muted-foreground py-2">End of standings ({leaderboard.length} teams)</p>
        )}
        <div className="mt-4">
          <AffiliationDisclaimer testId="rankings-affiliation-disclaimer" />
        </div>
      </div>

      {/* v6.x — Locked permanent self-row, now PINNED like the bottom tab
          bar (fixed above it) so it's always fully visible the moment you
          open Rankings — no scrolling to find where you stand.
          • Renders even when the user is already in the visible page.
          • Shows a rank like "#13,578 of 41,902" so the user always knows
            where they stand without scrolling through thousands of rows. */}
      {pinnedSelf && (
        <div
          className="fixed left-0 right-0 z-40 px-4 pointer-events-none"
          style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 72px)' }}
          data-testid="rankings-pinned-self-container"
        >
          <div className="pointer-events-auto rounded-xl border-2 border-[hsl(var(--accent)/0.45)] bg-background/95 backdrop-blur-md shadow-[0_-4px_20px_rgba(0,0,0,0.25)] p-2">
            <div className="flex items-center justify-between mb-1.5 px-1">
              <p className="text-[10px] font-semibold text-[hsl(var(--accent-text))] uppercase tracking-wider flex items-center gap-1" data-testid="pinned-self-your-team-label">
                <Pin className="h-3 w-3" fill="currentColor" /> Your team
              </p>
              <div className="flex items-center gap-1.5">
                {myRankInfo?.total_teams ? (
                  <p className="text-[10px] text-muted-foreground tabular-nums">
                    {pinnedSelf.rank
                      ? <>#{pinnedSelf.rank.toLocaleString()} of {myRankInfo.total_teams.toLocaleString()}</>
                      : <>Unranked · {myRankInfo.total_teams.toLocaleString()} ranked</>}
                  </p>
                ) : null}
                {/* v6.x — "Share your team" growth loop (utm_source=share). */}
                <ShareButton
                  path={`/team/${pinnedSelf.id}`}
                  title="My team on Fantasy World Star"
                  text={`Check out my team "${pinnedSelf.name || 'my squad'}" on Fantasy World Star!`}
                  iconOnly
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-[hsl(var(--accent))]"
                  testid="share-team-btn"
                />
              </div>
            </div>
            {/* 2026-07-23 (owner decision) — rankings are finalized-only, so an
                un-finalized team is invisible. Make the "why" + the fix loud. */}
            {pinnedSelf.finalized === false && !finalInfo && (
              <div
                className="mb-1.5 rounded-lg border border-[hsl(43,74%,52%)]/40 bg-[hsl(43,74%,52%)]/10 px-2.5 py-2"
                data-testid="rankings-finalize-nudge"
              >
                <p className="text-[11px] leading-snug text-[hsl(43,74%,62%)]">
                  <b>Not in the rankings yet.</b> Hit <b>Finalize</b> to claim your spot — one pick is
                  enough ({pinnedSelf.slots ?? 0}/18 picked; fill the rest later, free).
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-1.5 h-7 px-2.5 text-[11px] border-[hsl(43,74%,52%)]/50 text-[hsl(43,74%,62%)] hover:bg-[hsl(43,74%,52%)]/20"
                  onClick={() => onFinishTeam?.()}
                  data-testid="rankings-finalize-nudge-btn"
                >
                  Finish my team →
                </Button>
              </div>
            )}
            <RankRow team={pinnedSelf} rank={pinnedSelf.rank} onClick={onTeamClick} currentUserId={user?.id} me={user} onReport={handleReportPhoto} pinned final={!!finalInfo} delta={rankDeltas[pinnedSelf.id]} prevPoints={prevPointsRef.current?.[pinnedSelf.id]} />
          </div>
        </div>
      )}
    </div>
  );
}
